import { rem, Tooltip } from "@mantine/core";
import { isString } from "@tiptap/react";
import dayjs from "dayjs";
import { Fragment, type ReactNode } from "react";
import ImageAttachment from "src/components/pages/Reports/Report/ImageAttachment";
import BodyPartSummary from "src/components/shared/EntriesGenerator/Inputs/BodyPartSummary";
import GuernseyChildInput from "src/components/shared/GuernseyMulti/GuernseyChildInput";
import GuernseyMulti from "src/components/shared/GuernseyMulti/GuernseyMulti";
import GuernseySummaryWrapper from "src/components/shared/GuernseyMulti/GuernseySummaryWrapper";
import TriangleContent from "src/components/shared/TriangleCells/TriangleContent/TriangleContent";
import { type IUser } from "src/components/shared/UserList/UserList.types";
import { getOrigin } from "src/environment";
import VitalsInputRenderer from "src/generator/_Medical/Vitals/VitalsInputs/VitalsInputRenderer";
import VitalsListRenderer from "src/generator/_Medical/VitalsList/VitalsListRenderer";
import { getValueInFormat } from "src/generator/Coordinates/Coorinates.utils";
import { type SteeringPumpRowValue } from "src/generator/SteeringPumps/SteeringPumps.types";
import { timeRegex } from "src/generator/utils/datetime.utils";
import { getCheckListProgress } from "src/generator/utils/getCheckListProgress";
import { type ICheckList } from "src/interfaces";
import {
  oilrec1Data,
  oilrec2Data,
  pressureConverter,
  temperatureConverter,
  vesselTypeData,
} from "src/mock/selectOptions";
import { Flex } from "src/styled";
import { openBase64 } from "src/utils";
import { ENTRY_STATUS, entryStatusMap } from "src/utils/entryStatus";
import { getHistoricalEntryUser } from "src/utils/logbookUtils/historicalEntryUser";
import { toUtcCalendarDay } from "src/utils/normalizeDate";
import { convertDDMMtoMMDD } from "src/utils/timeUtils/convertDateFormat";

import { type IYachtData } from "./Report.types";

export const getPdfBody = async (
  element: HTMLDivElement,
  mode: "auto" | "entries" | "charts" = "auto",
) =>
  await new Promise<string>((resolve, reject) => {
    try {
      const copy = element.cloneNode(true) as HTMLDivElement;
      const hasCharts = !!copy.querySelector("#charts");
      const useCharts = mode === "charts" || (mode === "auto" && hasCharts);
      const result = useCharts ? generateCharts(copy) : generateEntries(copy);
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });

export const getPdfSize = (dir: "portrait" | "landscape") => {
  return dir === "portrait"
    ? { width: 8.27, height: 11.69 }
    : { width: 11.69, height: 8.27 };
};

export const generateCharts = (element: HTMLDivElement) => {
  element.querySelector("#entries")?.remove();

  const chartImgs = element.querySelectorAll(
    ".report-chart-img",
  ) as unknown as HTMLImageElement[];

  const charts = element.querySelectorAll(
    ".report-chart",
  ) as unknown as HTMLCanvasElement[];

  chartImgs.forEach(async img => {
    img.style.display = "block";
    img.style.width = "100%";
    img.style.height = "auto";
  });

  charts.forEach(chart => {
    chart.remove();
  });

  element
    .querySelectorAll(".chart-fullscreen-btn, .chart-canvas-holder")
    .forEach(el => {
      el.remove();
    });

  element.querySelectorAll(".chart-wrapper").forEach(wrapper => {
    (wrapper as HTMLElement).style.width = "100%";
  });

  element.querySelectorAll(".charts-list").forEach(list => {
    (list as HTMLElement).style.display = "block";
  });

  return element.innerHTML;
};

export const generateEntries = (element: HTMLDivElement) => {
  element.querySelector("#generator")?.remove();
  element.querySelector("#charts")?.remove();
  // element.style.fontFamily = "Arial";

  const entries = element.querySelector("#entries") as HTMLDivElement;
  if (entries) {
    entries.style.display = "block";
    entries.style.fontFamily = "Arial";
    entries.style.fontSize = "13px";
  }

  const stamp = element.querySelector("#stamp") as HTMLDivElement;
  if (stamp) {
    stamp.querySelector("p")!.style.color = "black";

    stamp.style.marginTop = "20px";
    if (stamp.dataset.placement === "pdfOnly") {
      stamp.style.display = "flex";
    }
    if (stamp.dataset.placement === "screenOnly") {
      stamp.remove();
    }
  }

  element.querySelectorAll("table").forEach(table => {
    table.style.borderCollapse = "collapse";
    table.style.width = "100%";
  });

  element.querySelectorAll("th, td").forEach(cell => {
    cell.style.borderCollapse = "collapse";
    cell.style.border = "1px solid black";
    if (cell.style.height === "0px") {
      cell.replaceWith("");
    }
  });

  element.querySelectorAll("tr").forEach(row => {
    row.style.breakInside = "avoid";
  });

  element.querySelectorAll(".item-hr").forEach((el: any) => {
    el.style.backgroundColor = "black";
  });

  element
    .querySelectorAll("strong, b, .section-title, th")
    .forEach((el: any) => {
      el.style.fontSize = "13px";
      el.style.setProperty("font-size", "13px", "important");
    });

  element.querySelectorAll("[style*='font-weight: 200']").forEach(el => {
    (el as HTMLElement).style.fontWeight = "bold";
  });

  element.querySelectorAll(".triangle").forEach(el => {
    const leftText = el.getAttribute("data-left");
    const rightText = el.getAttribute("data-right");
    el.innerHTML = "";
    if (leftText || rightText)
      el.textContent = `${leftText ?? ""} / ${rightText ?? ""}`;
  });

  const coloredElements = element.querySelectorAll(
    "th, tr,.entryName, .entry, .section-title, .attachment, .entryContent",
  );
  const buttons = element.querySelectorAll("button");
  const cells = element.querySelectorAll("td");

  const styleCell = (cell: Element) => {
    const tableCell = cell as HTMLTableCellElement;
    if (tableCell.classList.contains("file-action")) {
      tableCell.remove();
      return;
    }
    tableCell.style.backgroundColor = "";
    tableCell.style.color = "";
    if (tableCell.classList.contains("border-none")) {
      tableCell.style.borderWidth = "0px";
    } else {
      tableCell.style.borderWidth = "1px";
      tableCell.style.borderColor = "black";
      if (tableCell.classList.contains("bottom-border")) {
        tableCell.style.borderBottomStyle = "solid";
        tableCell.style.height = "50px";
      } else if (tableCell.classList.contains("entry")) {
        tableCell.style.borderWidth = "0px";
      } else {
        tableCell.style.borderStyle = "solid";
      }
    }
    if (tableCell.classList.contains("entryName")) {
      tableCell.style.borderRight = "2px solid black";
      tableCell.style.textTransform = "capitalize";
    }
    if (tableCell.classList.contains("entryContent")) {
      tableCell.style.borderTopWidth = "0px";
      tableCell.style.borderRight = "2px solid black";
    }

    tableCell.style.fontFamily = "Arial";
    tableCell.style.fontSize = "13px";
  };
  buttons.forEach(button => {
    button.style.backgroundColor = "transparent";
    button.style.border = "none";
    button.style.color = "black";
    button.style.textDecoration = "none";
  });

  coloredElements.forEach(styleCell);
  cells.forEach(styleCell);
  element.querySelectorAll(".attachment-link").forEach((el: any) => {
    el.style.color = "black";
  });
  element.querySelectorAll(".date-header").forEach((el: any) => {
    el.style.padding = "0 10px 0px 10px";
  });
  const checks = element.querySelectorAll(".check");

  checks.forEach(check => {
    (check as HTMLDivElement).style.borderColor = "";
    (check as HTMLDivElement).style.backgroundColor = (check as HTMLDivElement)
      .style.backgroundColor
      ? "black"
      : "";
  });

  const historyRows = element.querySelectorAll(".history-row");

  historyRows.forEach(row => {
    (row as HTMLDivElement).style.backgroundColor = "black";
  });
  element.querySelectorAll("p").forEach((el: any) => {
    el.style.fontFamily = "Arial";
    el.style.fontSize = "13px";
    if (el.querySelector("div"))
      el.querySelector("div")!.style.fontFamily = "Arial";
  });
  // Add signature img if needed (Waste Declaration Report)
  const signatureTd = element.querySelector(
    "td#signature",
  ) as HTMLTableCellElement;

  if (signatureTd) {
    const signatureImg = document.createElement("img");
    signatureImg.src = signatureTd.innerText;
    signatureImg.style.maxHeight = "40px";
    while (signatureTd.firstChild) {
      signatureTd.removeChild(signatureTd.firstChild);
    }
    signatureTd.appendChild(signatureImg);
  }

  // const landscapeTables = element.querySelectorAll(".landscape-table");
  //
  // if (landscapeTables) {
  //   landscapeTables.forEach(table => {
  //     table.style.transform = "scale(0.5)";
  //     table.style.transformOrigin = "top left";
  //   });
  // }
  return element.innerHTML;
};

export const renderList = (
  list: any[],
  selectedKey: string,
  suffix: string | undefined,
) => (
  <div>
    {list.map((item, i) => {
      let value: string | React.ReactNode = "";
      if (typeof item === "string") {
        value = item;
      } else if (item?.type === "text" || (item && "text" in item)) {
        value = item.text;
      } else if (item?.type === "attachment") {
        value = (
          <button
            style={{
              borderRadius: "4px",
              padding: "4px 8px",
              textUnderlineOffset: "2px",
              textDecoration: "underline",
            }}
            onClick={() =>
              openBase64(item.b64, item.filetype, item.filename, false)
            }>
            {`File: ${item?.filename}`}
          </button>
        );
      } else if (item && typeof item === "object") {
        if (
          (item.key && item.key === selectedKey) ||
          typeof item.value === "string"
        )
          value = item.value;
        else if ("sys" in item || "dia" in item) {
          Object.entries(item).forEach(([k, v]) => {
            if (k === "sys" || k === "dia") value += `${v} ${k.toUpperCase()} `;
          });
        }
      }
      return (
        <p style={{ margin: "5px 0" }} key={i}>
          {value} {suffix ?? ""}
        </p>
      );
    })}
  </div>
);

export const defaultValue = (col: any) => {
  if (!col) return null;
  if (typeof col.value === "object" && !col.value?.value) {
    // Handle DualTimeInput format - don't try to join these values
    if (col.value?.timeLt || col.value?.timeUtc) {
      return "";
    }
    return "";
  }
  let value =
    col.value && typeof col.value === "object"
      ? `${Object.values(col.value).join(" ")}`
      : (col.value ?? "");

  if (col.suffix && col.suffix !== col.value?.unit) value += ` ${col.suffix}`;
  return value;
};

const getUserProp: (
  users: IUser[],
  id: string,
  props: Array<keyof IUser> | keyof IUser,
) => Partial<Record<keyof IUser, string>> = (users, id, props) => {
  if (!id) return {};
  if (!props || props.length === 0) return {};
  if (!users || users.length === 0) return {};
  const user = users.find(u => u.userid === id);
  if (!user) return {};
  if (typeof props === "string") props = [props];
  return props.reduce(
    (acc, prop) => {
      acc[prop] = String(user[prop]);
      return acc;
    },
    {} as Record<keyof IUser, string>,
  );
};

const getUser = (
  value: string | Record<keyof IUser, string>,
  users: IUser[],
  name: string = "",
) => {
  if (typeof value === "string") {
    const user = users.find(u => u.userid === value);
    if (!user && name && name.split(" ").length > 1) {
      return getHistoricalEntryUser(name, value);
    }
    return user;
  } else if (typeof value === "object") {
    if (value?.firstname && value?.surname) {
      if (!value?.ref)
        value = {
          ...value,
          ref: getUserProp(users, value.userid, "ref").ref ?? "",
        };
      if (!value?.rolename)
        value = {
          ...value,
          rolename: getUserProp(users, value.userid, "rolename").rolename ?? "",
        };
      return value;
    }
    return users.find(u => u?.userid === value?.userid);
  } else {
    return "";
  }
};

const transformOilrec = (col: any) => {
  const key =
    typeof col.value === "string"
      ? col.value
      : col.value?.key
        ? col.value.key
        : col.value?.value?.key;
  return (
    <span>
      <p>{transformSelect(col.unit, key)} </p>
      {col?.value?.data?.tankName && <p>{col.value.data.tankName ?? ""} </p>}
    </span>
  );
};

export const getColValue = (
  col: any,
  users?: IUser[],
  mainColor?: string,
  yachtdata?: IYachtData | null,
  uppercase?: boolean,
  checkLists?: ICheckList[],
  rowIndex?: number,
  lbeid?: string,
  pdfEntry?: boolean,
) => {
  if (!col) {
    return <div />;
  }
  // if ("value" in col && (col.value === "" || col.value === null)) return "";

  let renderedItem: ReactNode = <></>;

  if (col?.unit) {
    let unit = col.unit;
    if (col.unit.includes("vitals")) unit = "vitals";
    if (col.tag === "med_dob") unit = "date";
    const renderValue = () => {
      switch (unit) {
        case "date": {
          let value =
            (col.value && typeof col.value === "object"
              ? col.value.value
              : col.value) ?? "";

          if (value) {
            if (col.tag === "med_dob") {
              if (dayjs(value).isValid()) {
                const dateOnly = value.substring(0, 10);
                let dateFormat = "DD MMM YYYY";
                if (col.showWeekday) dateFormat += " dddd";
                return dayjs(`${dateOnly}T00:00:00`).format(dateFormat);
              } else {
                return defaultValue(col);
              }
            }
            let dateFormat = "DD MMM YYYY";
            if (col.showWeekday) dateFormat += " dddd";
            value = dayjs(convertDDMMtoMMDD(value)).format(dateFormat);
          }

          return value;
        }
        case "vital_list":
          if (
            col.list.length === 1 &&
            (!col.list[0]?.value || Object.keys(col.list[0].value).length === 0)
          )
            return;
          const renderInput = (index: number, item: any) =>
            pdfEntry ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "5px",
                  color: item.changed
                    ? item.changedType === "edit"
                      ? "red"
                      : "green"
                    : "auto",
                }}>
                <Tooltip label={`${item.date} ${item.time}`}>
                  <p
                    style={{ margin: 0, fontSize: "14px", fontWeight: "bold" }}>
                    {item.time} -
                  </p>
                </Tooltip>
                {col.tag === "med_pressure" ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: "2px",
                    }}>
                    <p style={{ margin: 0 }}>
                      {item?.value?.dia ?? ""}/{item?.value?.sys ?? ""}
                    </p>
                  </div>
                ) : (
                  <p style={{ margin: 0 }}>
                    {item?.value ?? ""} {col.suffix ?? ""}
                  </p>
                )}
              </div>
            ) : (
              <Flex
                direction="column"
                align="center"
                style={{
                  color: item.changed
                    ? item.changedType === "edit"
                      ? "red"
                      : "green"
                    : "auto",
                }}>
                <Tooltip label={`${item.date} ${item.time}`}>
                  <p style={{ margin: 0, fontSize: rem(10) }}> {item.time}</p>
                </Tooltip>
                {col.tag === "med_pressure" ? (
                  <Flex gap={rem(15)} direction="column">
                    <p style={{ margin: 0 }}>{item?.value?.dia ?? ""}</p>
                    <p style={{ margin: 0 }}>{item?.value?.sys ?? ""}</p>
                  </Flex>
                ) : (
                  <p style={{ margin: 0 }}>{item?.value ?? ""}</p>
                )}
              </Flex>
            );
          return pdfEntry ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {col.list.map((item: any, index: number) => (
                <Fragment key={index}>{renderInput(index, item)}</Fragment>
              ))}
            </div>
          ) : (
            <VitalsListRenderer
              list={col.list}
              renderInput={renderInput}
              tag={col.tag}
              suffix={col.suffix}
            />
          );
        case "triangle":
          const data: string[] =
            typeof col.value === "string" || !col.value
              ? []
              : Object.values(col.value);
          return (
            <div className="triangle" data-left={data[0]} data-right={data[1]}>
              <TriangleContent data={data} minHeight={70} isInput coloredText />
            </div>
          );
        case "multilist":
        case "list":
          const value =
            (Array.isArray(col.value)
              ? col.value
              : typeof col.value === "string"
                ? col.value
                : col.value?.value
                  ? col.value?.value
                  : col.value?.key) ?? "";
          return (
            <div>
              {Array.isArray(value) ? (
                value.map(item => (
                  <p key={item.key}>{item.value ? item.value : item.key}</p>
                ))
              ) : (
                <p>{value}</p>
              )}
            </div>
          );
        case "guernseyMulti":
          return (
            <GuernseyMulti
              content={col.content}
              value={col.value}
              rowIndex={rowIndex}
              lbeid={lbeid}
              summary
            />
          );
        case "guernsey-child":
          return (
            <GuernseySummaryWrapper col={col} uppercase={uppercase}>
              <GuernseyChildInput
                tag={col.tag}
                rowIndex={rowIndex}
                summary
                lbeid={lbeid}
              />
            </GuernseySummaryWrapper>
          );
        case "vitals":
          return (
            <VitalsInputRenderer
              unit={col.unit as string}
              items={col.value}
              suffix={col.suffix}
              disabled
            />
          );
        case "check":
          return (
            <div
              className="check"
              style={{
                width: "14px",
                height: "14px",
                border:
                  `1px solid ` +
                  (col.changed && col.value
                    ? col.changedType === "edit"
                      ? "red"
                      : "green"
                    : `${mainColor}`),
                backgroundColor: col.changed
                  ? col.changedType === "edit"
                    ? col.value
                      ? "red"
                      : ""
                    : col.value
                      ? "green"
                      : ""
                  : col.value
                    ? `${mainColor}`
                    : "",
                display: !!col.value ? "block" : "none",
              }}
            />
          );
        case "med_complaint":
          return <BodyPartSummary value={col.value} pdfEntry={pdfEntry} />;
        case "case/report":
          if (typeof col.value === "string") return col.value;
          if (col.value.report)
            return (
              <div style={{ textAlign: "left" }}>
                <p>{col.value.caseno ?? ""}</p>
                {col.value.report.length > 0 && <p>Files: </p>}
                {col.value.report.map((report: any, i: number) => (
                  <p key={`report-${i}`}>{report.filename}</p>
                ))}
              </div>
            );
          return "";
        case "sig":
        case "large/text":
          if (col.value?.content) return null;
          return (
            <div style={{ textAlign: "left" }}>
              {col.value?.split("\n").map(
                (line: string, i: number) =>
                  line !== "" && (
                    <span key={`desc-c-${i}`}>
                      {line}
                      <br />
                    </span>
                  ),
              )}
            </div>
          );

        case "user":
        case "userref":
        case "master":
        case "Captain/Master":
        case "officer":
        case "engineers":
        case "medOfficer":
        case "signature":
        case "userrefpopiom":
        case "users": {
          if (!col && !col.value) return "";
          const user = getUser(
            typeof col.value === "object" ? col.value : col.value,
            users ?? [],
            col.name,
          );
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {user && !col.value?.manualValue
                ? `${user.ref && user.ref + " - "}${col.showRanks && user.rolename ? user.rolename + " " : ""}${user.firstname} ${user.surname}`
                : ""}
              {col.value?.manualValue ? col.value?.manualValue : ""}
              {user &&
                col.value?.extraValue &&
                Object.keys(col.value.extraValue).map(key => (
                  <span key={key} style={{ fontSize: 14 }}>
                    {`${key == "vhf" ? "UHF CH" : key}: ${col.value.extraValue[key]}`}
                  </span>
                ))}
              {col.signature &&
                user &&
                "signature" in user &&
                user.signature &&
                !col.hideSig && (
                  <img
                    height={"40px"}
                    style={{ objectFit: "contain", maxWidth: "100%" }}
                    src={user.signature as string}
                  />
                )}
            </div>
          );
        }
        case "userrefpop":
        case "userrefpopname": {
          const user = getUser(col.value, users ?? []);
          return user ? `${user.ref}` : "";
        }
        case "userrank": {
          const user: any = getUser(col.value, users ?? []);
          return user
            ? `${user.ref} - ${user.firstname} ${user.surname} - ${user.rolename}`
            : "";
        }
        case "sigbox": {
          const user = getUser(col.value, users ?? []);
          if (!user) return "";
          return (
            <>
              <p
                style={{
                  margin: 0,
                }}>{`${user.ref} - ${user.firstname} ${user.surname}`}</p>
              {"signature" in user && user?.signature && !col.hideSig && (
                <img
                  height={"40px"}
                  style={{ objectFit: "contain", maxWidth: "100%" }}
                  src={user.signature}
                />
              )}
            </>
          );
        }
        case "temperature": {
          const unit =
            temperatureConverter.find(u => u.value === col.value?.unit)
              ?.label ?? "°C";
          return (
            <span style={{ textTransform: "none" }}>
              {defaultValue({
                ...col,
                value: {
                  value:
                    typeof col.value === "object" ? col.value.value : col.value,
                  unit,
                },
              })}
            </span>
          );
        }
        case "pressure": {
          const unit =
            pressureConverter.find(u => u.value === col.value?.unit)?.label ??
            "mB";

          return (
            <span style={{ textTransform: "none" }}>
              {defaultValue({
                ...col,
                value: {
                  value:
                    typeof col.value === "object" ? col.value.value : col.value,
                  unit,
                },
              })}
            </span>
          );
        }
        case "time":
          if (!col.value) return "";
          if (typeof col.value === "string")
            return transformTime(col.value, col.suffix ?? "LT");

          // Handle DualTimeInput format (timeLt and timeUtc properties)
          if (col.value?.timeLt && col.value?.timeUtc) {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                }}>
                <span>{transformTime(col.value.timeLt, "LT")}</span>
                <span>{transformTime(col.value.timeUtc, "UTC")}</span>
              </div>
            );
          }

          // Handle single DualTimeInput value (either LT or UTC)
          if (col.value?.timeLt) {
            return transformTime(col.value.timeLt, "LT");
          }
          if (col.value?.timeUtc) {
            return transformTime(col.value.timeUtc, "UTC");
          }

          return transformTime(col.value?.time, getSuffix(col.value?.type));
        case "24lock":
          return col.value?.time && col.value.time !== ":"
            ? `${transformTime(col.value.time)} ${getSuffix(col.value.type)}`
            : "";
        case "lat":
          return transformOilCoordinate(col.value, true);
        case "lat2":
          return transformCoordinate(col.value, true, false);
        case "long":
          return transformOilCoordinate(col.value, false);
        case "long2":
          return transformCoordinate(col.value, false, true);
        case "long3":
          return transformCoordinate(col.value, false, false, true);
        case "lat3":
          return transformCoordinate(col.value, false, false, false, true);
        case "long4":
        case "lat4":
          return transformCoordinate(
            col.value,
            false,
            false,
            false,
            false,
            true,
          );
        case "locport":
        case "loclookup":
          return (
            <div style={{ display: "flex", gap: "20px" }}>
              {col.value && <div>{col.value ?? ""}</div>}
              {col.portCode && <div>{col.portCode ?? ""}</div>}
            </div>
          );
        case "oilrec1":
        case "oilrec2":
          return transformOilrec(col);
        case "sec":
        case "secstatus":
        case "cidrills":
        case "pov":
        case "boolna":
        case "gmdss-opts":
        case "ozonetype":
        case "at":
        case "shiptype":
        case "vesseltype":
          return transformSelect(col.unit, col.value);
        case "tanks":
          if (typeof col.value === "string") {
            return col.value;
          } else return transformSelect(col.unit, col.value);
        case "m3":
          return (
            <span style={{ textTransform: "lowercase" }}>
              {col.value} m<sup>3</sup>
            </span>
          );
        case "yachtname":
        case "port":
        case "sign":
        case "imo":
        case "gross":
        case "net":
        case "ofnum":
        case "mmsi":
        case "len":
          return (
            col.value ??
            (yachtdata
              ? yachtdata[
                  (col.unit === "ofnum" ? "num" : col.unit) as keyof IYachtData
                ]
              : "")
          );
        case "dp_equip":
          return col.value ?? (yachtdata ? yachtdata?.dynpos?.equip : "");
        case "dp_equipclass":
          return col.value ?? (yachtdata ? yachtdata?.dynpos?.equipclass : "");
        case "bioareas":
          if (!yachtdata) return;
          return transformBioarea(col.value, yachtdata);
        case "bloodpressure":
          return (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
              <span style={{ margin: 0 }}>{`${col.value?.sys ?? ""} SYS`}</span>
              <span style={{ margin: 0 }}>{`${col.value?.dia ?? ""} DIA`}</span>
            </div>
          );
        case "BLANK":
          return col.id === "signature" ? (
            col.value
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                textTransform: "none",
                textAlign: (col.align as any) ?? "left",
              }}>
              <span style={{ fontWeight: col.bold ? "bold" : "normal" }}>
                {col.name}
              </span>
              {col.small && <span style={{ fontSize: 12 }}>{col.small}</span>}
            </div>
          );
        case "steering.pumps":
          if (!col.value || !Array.isArray(col.value)) return <></>;
          return (
            <>
              {col.value.map(
                (pump: SteeringPumpRowValue) =>
                  pump && (
                    <div
                      key={pump.label}
                      style={{ display: "flex", gap: "8px" }}>
                      <span style={{ textTransform: "none" }}>
                        {pump.label}
                      </span>
                      <div
                        className="check"
                        style={{
                          width: "20px",
                          height: "20px",
                          border: `1px solid ${mainColor}`,
                          backgroundColor: pump.check ? mainColor : "",
                        }}
                      />
                      <span>{transformTime(pump.time)}</span>
                    </div>
                  ),
              )}
            </>
          );
        case "checklist": {
          if (!col.value) return <></>;
          let name = col.value.name;
          if (checkLists) {
            const checkList = checkLists.find(i => i.cid === col.value.cid);
            if (checkList)
              name +=
                checkList.data.type !== "group"
                  ? ` ${getCheckListProgress(checkList.data.list)}`
                  : "";
          }
          return <span>{name}</span>;
        }
        case "attach":
          return <ImageAttachment value={col.value} />;
        case "link":
          const origin = getOrigin();
          const href = `${origin}${origin.endsWith("/") ? "" : "/"}${col.href}`;
          return (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              style={{ color: "black", textDecoration: "underline" }}>
              {col.hname}
            </a>
          );
        case "medAttached":
          if (!col.value) return "";
          if (typeof col.value === "string") return col.value;
          return (
            <div style={{ textAlign: "left" }}>
              <p>{col.value.text ?? ""}</p>
              {col.value.files?.length > 0 && <p>Files: </p>}
              {col.value.files?.map((report: any, i: number) => (
                <p key={`file-${i}`}>{report.filename}</p>
              ))}
            </div>
          );
        case "sub":
          if (col.cols && col.type === "split") {
            return (
              <span>
                {col.cols
                  .map((el: any) => typeof el.value === "string" && el.value)
                  .join(" | ")}
              </span>
            );
          }
        default:
          return defaultValue(col);
      }
    };
    renderedItem = renderValue();
  } else {
    renderedItem = defaultValue(col);
  }
  return (
    <div style={{ textTransform: uppercase ? "uppercase" : "none" }}>
      {renderedItem}
    </div>
  );
};
const getSuffix = (type: string) =>
  ["LT", "UTC"].includes(type) ? type : "LT";
export const transformBioarea = (
  value: string,
  yachtdata: IYachtData | null,
) => {
  const parsedValue =
    typeof value === "string"
      ? value
      : typeof value === "object"
        ? (value as any).value
        : "";
  let sectionName = "";

  if (yachtdata?.biofoul?.areas) {
    for (const section of yachtdata.biofoul.areas) {
      for (const item of section.list) {
        if (item.name === parsedValue) {
          sectionName = section.section;
          break;
        }
      }
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {sectionName && <span style={{ fontSize: 12 }}>{sectionName}</span>}
      {parsedValue && <span>{parsedValue}</span>}
    </div>
  );
};

export const renderReviewStatus = (status: number) => {
  switch (status) {
    case ENTRY_STATUS.DRAFT:
      return "Open / Auto Save";
    case ENTRY_STATUS.OPEN:
      return entryStatusMap[ENTRY_STATUS.OPEN].label;
    case ENTRY_STATUS.REVERIFY:
      return "Re-Verify / Auto Save";
    case ENTRY_STATUS.VERIFIED:
      return entryStatusMap[ENTRY_STATUS.VERIFIED].label;
    case ENTRY_STATUS.WITHDRAWN:
      return entryStatusMap[ENTRY_STATUS.WITHDRAWN].label;
    case ENTRY_STATUS.PENDING:
      return entryStatusMap[ENTRY_STATUS.PENDING].label;
  }
};

export const transformTime = (value: string, suffix?: string) => {
  const regex = timeRegex;
  if (!value || !regex.test(value)) {
    return "";
  }
  let transformedTime = `${value}`;
  if (suffix) {
    transformedTime += ` ${suffix}`;
  }
  return transformedTime;
};

export const transformCoordinate = (
  value: any,
  isLat: boolean,
  isLong: boolean,
  isLong3?: boolean,
  isLat3?: boolean,
  isLatLong4?: boolean,
) => {
  if (!value) return "";
  // NOTE added value.nums where the old code was just value.
  // Need to check older formats in case of impact.
  // isString(value) for older and other formats ie bridge log.
  const dir = value.dir ?? "";
  value = isString(value)
    ? value
    : value?.nums?.toString().replaceAll("undefined", "");
  const splitted = getValueInFormat(
    value,
    isLong,
    isLat,
    Boolean(isLong3),
    Boolean(isLat3),
    Boolean(isLatLong4),
  );
  if (splitted.every((el: string) => el.length === 0)) return "";
  return `${splitted[0]}\u00b0${splitted[1]}'${splitted[2]}" ${dir}`;
};

export const transformOilCoordinate = (value: any, isLat: boolean) => {
  if (!value) return "";

  if (typeof value === "string") {
    const splitted = getValueInFormat(value, !isLat, isLat, false, false);
    if (splitted.every((el: string) => el.length === 0)) return "";
    return `${splitted[0]}\u00b0${splitted[1]}'${splitted[2]}"`;
  }

  if (typeof value === "object") {
    const nums = value.nums ?? "";
    const dir = value.dir ?? "";
    const splitted = getValueInFormat(nums, !isLat, isLat, false, false);
    if (splitted.every((el: string) => el.length === 0)) return "";
    return `${splitted[0]}\u00b0${splitted[1]}'${splitted[2]}" ${dir}`;
  }
};

export const transformSelect = (
  unit: string,
  value: string | { value: string },
) => {
  if (typeof value === "string") {
    switch (unit) {
      case "sec":
        return transformSec(value);
      case "secstatus":
        return transformSecStatus(value);
      case "cidrills":
        return transformCiDrills(value);
      case "pov":
        return transformPov(value);
      case "boolna":
        return transformBoolna(value);
      case "gmdss-opts":
        return transformGmdssOpts(value);
      case "oilrec1":
        return transformOilRec1(value);
      case "oilrec2":
        return transformOilRec2(value);
      case "ozonetype":
        return transformOzonetype(value);
      case "at":
        return transformAt(value);
      case "shiptype":
        return transformShiptype(value);
      case "vesseltype":
        return transformVesseltype(value);
    }
  } else if (typeof value === "object" && value?.value) {
    return typeof value.value === "string"
      ? value.value
      : (value.value?.value ?? value.value?.key ?? "");
  } else {
    return "";
  }
};

export const transformSec = (value: string) => {
  const data: Record<string, string> = {
    SEC1: "Security Level 1",
    SEC2: "Security Level 2",
    SEC3: "Security Level 3",
  };
  return data[value];
};

export const transformSecStatus = (value: string) => {
  const data: Record<string, string> = {
    "1": "Security Level 1",
    "2": "Security Level 2",
    "3": "Security Level 3",
  };
  return data[value];
};

export const transformCiDrills = (value: string) => {
  const data: Record<string, string> = {
    "1": "Emergency steering drill at least once every 3 months - SOLAS V/26.4",
    "2": "Check and test of steering gear within 12 hours before departure - SOLAS V/26.1 and 26.2",
    "3": "Other (inc Weekly for ships regularly engaged in short voyages)",
  };
  return data[value];
};

export const transformPov = (value: string) => {
  const data: Record<string, string> = {
    POV1: "IMO",
    POV2: "Official No",
    POV3: "PLN",
  };
  return data[value];
};

export const transformBoolna = (value: string) => {
  const data: Record<string, string> = {
    "1": "Yes",
    "2": "No",
    "3": "NA",
  };
  return data[value];
};

export const transformAt = (value: string) => {
  const data: Record<string, string> = {
    "1": "Port",
    "2": "Sea",
    "3": "Anchor",
    "4": "Dry Dock",
  };
  return data[value];
};

export const transformGmdssOpts = (value: string) => {
  const data: Record<string, string> = {
    "0": "",
    "1": "N/A",
    "2": "Daily",
    "3": "Weekly",
    "4": "Monthly",
  };
  return data[value];
};

export const transformArrayToRecord = (
  array: Array<{ value: string; label: string }>,
): Record<string, string> => {
  const result: Record<string, string> = {};
  array.forEach(item => {
    result[item.value] = item.label;
  });
  return result;
};

export const transformOilRec1 = (value: string) => {
  const data = transformArrayToRecord(oilrec1Data);
  return data[value];
};

export const transformOilRec2 = (value: string) => {
  const data = transformArrayToRecord(oilrec2Data);
  return data[value];
};

export const transformOzonetype = (value: string) => {
  const data: Record<string, string> = {
    REF: "Refrigerant",
    RFD: "Refrigerant Discharge",
    LKT: "Leak Test",
  };
  return data[value];
};

export const transformShiptype = (value: string) => {
  const data: Record<string, string> = {
    "1": "Passenger Ship",
    "2": "Oil Tanker",
    "3": "Chemical Tanker",
    "4": "Bulk Carrier",
    "5": "Container",
    "6": "Other Cargo Ship",
    "7": "Ro-ro",
    "8": "Other (specify)",
  };
  return data[value];
};

export const transformVesseltype = (value: string) => {
  const data: Record<string, string> = {};
  for (const item of vesselTypeData) {
    data[item.value] = item.label;
  }
  return data[value];
};

export const getTimeline = (type: number, start: string, end: string) => {
  const today = dayjs();
  if (type === 0) {
    const start = dayjs().subtract(1, "month");
    return `Last Month (${start.format("D MMM YYYY")} - ${today.format(
      "D MMM YYYY",
    )})`;
  } else if (type === 1) {
    const start = dayjs().subtract(3, "month");
    return `Last 3 Months (${start.format("D MMM YYYY")} - ${today.format(
      "D MMM YYYY",
    )})`;
  } else if (type === 2) {
    const start = dayjs().subtract(6, "month");
    return `Last 6 Months (${start.format("D MMM YYYY")} - ${today.format(
      "D MMM YYYY",
    )})`;
  } else if (type === 3) {
    const start = dayjs().subtract(12, "month");
    return `Last 12 Months (${start.format("D MMM YYYY")} - ${today.format(
      "D MMM YYYY",
    )})`;
  } else if (start && end) {
    const formattedStart = dayjs(toUtcCalendarDay(start)).format("D MMM YYYY");
    const formattedEnd = dayjs(toUtcCalendarDay(end)).format("D MMM YYYY");
    return `${formattedStart} - ${formattedEnd}`;
  }
};
